import React, { useState, useRef, useEffect } from 'react';
import { Trash2, Copy, RotateCcw, Plus, ArrowRight, ArrowLeftRight } from 'lucide-react';
import { Col } from "reactstrap";
import { useDarkMode } from 'context/DarkModeProvider';
import { useLanguage } from 'context/LanguageProvider';
import '../../assets/css/CreateYourOwn/listBuilder.css';
import CodeHighlighter from 'components/CodeHighlighter';

const LinkedListBuilder = () => {
    const [listType, setListType] = useState('singly');
    const [nodes, setNodes] = useState([
        { id: 1, value: 'Head', x: 150, y: 200 }
    ]);
    const [nextId, setNextId] = useState(2);
    const [selectedNode, setSelectedNode] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [newNodeValue, setNewNodeValue] = useState('');
    const [animatingNode, setAnimatingNode] = useState(null);
    const [codeLanguage, setCodeLanguage] = useState('javascript');
    const [draggedNode, setDraggedNode] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [hoverNode, setHoverNode] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [deleteMode, setDeleteMode] = useState(false);
    const [actionLog, setActionLog] = useState([]);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const containerRef = useRef(null);
    const [showInfo, setShowInfo] = useState(false);
    const [copied, setCopied] = useState(false);
    const { darkMode } = useDarkMode();
    const { selLanguage } = useLanguage();

    const nodeWidth = 130;
    const nodeHeight = 80;
    const nodeGap = 185;
    const colors = ['#667eea'];

    const animateOperation = (operation, nodeId = null) => {
        setAnimatingNode({ operation, nodeId });
        setTimeout(() => setAnimatingNode(null), 600);
    };

    const addNode = (value) => {
        const lastNode = nodes[nodes.length - 1];
        const nodeVal = value || `Node ${nextId}`;
        const newNode = {
            id: nextId,
            value: nodeVal,
            x: lastNode.x + nodeGap,
            y: lastNode.y,
            color: colors[Math.floor(Math.random() * colors.length)]
        };
        setNodes(prev => [...prev, newNode]);
        setNextId(prev => prev + 1);
        animateOperation('add', newNode.id);
        setNewNodeValue('');
        setActionLog(prev => [...prev, { type: 'addNode', value: nodeVal }]);
    };

    const insertAfter = (nodeId, value = null) => {
        const nodeIndex = nodes.findIndex(n => n.id === nodeId);
        if (nodeIndex === -1) return;
        const nodeVal = value || `Node ${nextId}`;
        const newNode = {
            id: nextId,
            value: nodeVal,
            x: nodes[nodeIndex].x + nodeGap,
            y: nodes[nodeIndex].y,
            color: colors[Math.floor(Math.random() * colors.length)]
        };
        const updatedNodes = [...nodes];
        for (let i = nodeIndex + 1; i < updatedNodes.length; i++) updatedNodes[i].x += nodeGap;
        updatedNodes.splice(nodeIndex + 1, 0, newNode);
        setNodes(updatedNodes);
        setNextId(prev => prev + 1);
        animateOperation('insert', newNode.id);
        const afterNode = nodes[nodeIndex];
        setActionLog(prev => [...prev, { type: 'insertAfter', value: nodeVal, after: afterNode.value }]);
    };

    const deleteNode = (nodeId) => {
        if (nodes.length <= 1) return;
        const nodeIndex = nodes.findIndex(n => n.id === nodeId);
        if (nodeIndex === -1) return;
        const deletedValue = nodes[nodeIndex].value;
        const updatedNodes = [...nodes];
        for (let i = nodeIndex + 1; i < updatedNodes.length; i++) updatedNodes[i].x -= nodeGap;
        setNodes(prev => prev.filter(n => n.id !== nodeId));
        if (selectedNode === nodeId) setSelectedNode(null);
        animateOperation('delete', nodeId);
        setDeleteMode(false);
        setActionLog(prev => [...prev, { type: 'deleteNode', value: deletedValue }]);
    };

    const editNode = (nodeId, newValue) => {
        const oldValue = nodes.find(n => n.id === nodeId)?.value;
        setNodes(prev => prev.map(node => node.id === nodeId ? { ...node, value: newValue } : node));
        setEditingId(null);
        setActionLog(prev => [...prev, { type: 'editNode', oldValue, newValue }]);
    };

    const arrangeNodes = () => {
        setNodes(prev => prev.map((node, index) => ({ ...node, x: 150 + index * nodeGap, y: 150 })));
        setPan({ x: 0, y: 0 });
        animateOperation('arrange');
    };

    const clearAll = () => {
        setNodes([{ id: 1, value: 'Head', x: 150, y: 200, color: colors[0] }]);
        setNextId(2);
        setSelectedNode(null);
        setDeleteMode(false);
        setPan({ x: 0, y: 0 });
        animateOperation('clear');
        setActionLog(prev => [...prev, { type: 'clear' }]);
    };

    const handleZoomIn  = () => setZoom(prev => Math.min(parseFloat((prev + 0.1).toFixed(1)), 2.0));
    const handleZoomOut = () => setZoom(prev => Math.max(parseFloat((prev - 0.1).toFixed(1)), 0.3));

    // Scroll to zoom
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const handleWheel = (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            setZoom(prev => Math.min(2.0, Math.max(0.3, parseFloat((prev + delta).toFixed(1)))));
        };
        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => container.removeEventListener('wheel', handleWheel);
    }, []);

    // ── Node dragging ────────────────────────────────────────────────────────
    const handleMouseDown = (e, node) => {
        if (deleteMode) return;
        e.stopPropagation();
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const mx = (e.clientX - rect.left) / zoom - pan.x / zoom;
        const my = (e.clientY - rect.top)  / zoom - pan.y / zoom;
        setDraggedNode(node.id);
        setDragOffset({ x: mx - node.x, y: my - node.y });
        setSelectedNode(node.id);
    };

    useEffect(() => {
        if (draggedNode === null) return;
        const handleMouseMove = (e) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const mx = (e.clientX - rect.left) / zoom - pan.x / zoom;
            const my = (e.clientY - rect.top)  / zoom - pan.y / zoom;
            setNodes(prev => prev.map(n => n.id === draggedNode ? { ...n, x: mx - dragOffset.x, y: my - dragOffset.y } : n));
        };
        const handleMouseUp = () => setDraggedNode(null);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    }, [draggedNode, dragOffset.x, dragOffset.y, zoom, pan]);

    // ── Canvas panning ───────────────────────────────────────────────────────
    const handleCanvasMouseDown = (e) => {
        if (draggedNode !== null) return;
        if (deleteMode) return;
        // Only pan on left-click directly on the canvas (not on a node)
        if (e.target !== containerRef.current && !e.target.closest('.linked-list-canvas > div')) return;
        setIsPanning(true);
        setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    };

    useEffect(() => {
        if (!isPanning) return;
        const handleMouseMove = (e) => {
            setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
        };
        const handleMouseUp = () => setIsPanning(false);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    }, [isPanning, panStart]);

    const handleNodeClick = (e, node) => {
        if (deleteMode) deleteNode(node.id);
        else setSelectedNode(node.id);
    };

    // ── Keyboard shortcuts ───────────────────────────────────────────────────
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.ctrlKey || e.metaKey) {
                switch (e.key) {
                    case 'Enter':    e.preventDefault(); if (newNodeValue.trim()) addNode(newNodeValue); break;
                    case 'Backspace':e.preventDefault(); if (nodes.length > 1) deleteNode(nodes[nodes.length - 1].id); break;
                    case 'r':        e.preventDefault(); arrangeNodes(); break;
                }
            }
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [newNodeValue, nodes]);

    // ── Connections ──────────────────────────────────────────────────────────
    const renderConnections = () => {
        const connections = [];
        for (let i = 0; i < nodes.length - 1; i++) {
            const cur = nodes[i], nxt = nodes[i + 1];
            const sx = cur.x + nodeWidth, sy = cur.y + nodeHeight / 2;
            const ex = nxt.x, ey = nxt.y + nodeHeight / 2;
            connections.push(
                <div key={`next-${cur.id}-${nxt.id}`}>
                    <svg style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}>
                        <line x1={sx} y1={sy} x2={ex} y2={ey} stroke="#4299e1" strokeWidth="3" />
                        <polygon points={`${ex - 12},${ey - 5} ${ex},${ey} ${ex - 12},${ey + 5}`} fill="#4299e1" />
                    </svg>
                    <div style={{ position: 'absolute', left: (sx + ex) / 2 - 15, top: Math.min(sy, ey) - 25, fontSize: '10px', color: '#4299e1', fontWeight: 'bold', zIndex: 3 }}>next</div>
                </div>
            );
            if (listType === 'doubly') {
                const psx = nxt.x, psy = nxt.y + nodeHeight / 2 + 15;
                const pex = cur.x + nodeWidth, pey = cur.y + nodeHeight / 2 + 15;
                connections.push(
                    <div key={`prev-${nxt.id}-${cur.id}`}>
                        <svg style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}>
                            <line x1={psx} y1={psy} x2={pex} y2={pey} stroke="#e53e3e" strokeWidth="3" />
                            <polygon points={`${pex + 12},${pey - 5} ${pex},${pey} ${pex + 12},${pey + 5}`} fill="#e53e3e" />
                        </svg>
                        <div style={{ position: 'absolute', left: (psx + pex) / 2 - 15, top: psy + 10, fontSize: '10px', color: '#e53e3e', fontWeight: 'bold', zIndex: 3 }}>prev</div>
                    </div>
                );
            }
        }
        return connections;
    };

    // ── Code generation ──────────────────────────────────────────────────────
    const generateCode = () => {
        const renderActions = (lang) => {
            return actionLog.map(action => {
                if (action.type === 'addNode') {
                    if (lang === 'javascript') return `myList.append("${action.value}");`;
                    if (lang === 'python')     return `    my_list.append("${action.value}")`;
                    if (lang === 'java')       return `        myList.append("${action.value}");`;
                    if (lang === 'cpp')        return `    myList.append("${action.value}");`;
                }
                if (action.type === 'insertAfter') {
                    if (lang === 'javascript') return `myList.insertAfter("${action.after}", "${action.value}");`;
                    if (lang === 'python')     return `    my_list.insert_after("${action.after}", "${action.value}")`;
                    if (lang === 'java')       return `        myList.insertAfter("${action.after}", "${action.value}");`;
                    if (lang === 'cpp')        return `    myList.insertAfter("${action.after}", "${action.value}");`;
                }
                if (action.type === 'deleteNode') {
                    if (lang === 'javascript') return `myList.delete("${action.value}");`;
                    if (lang === 'python')     return `    my_list.delete("${action.value}")`;
                    if (lang === 'java')       return `        myList.delete("${action.value}");`;
                    if (lang === 'cpp')        return `    myList.deleteValue("${action.value}");`;
                }
                if (action.type === 'editNode') {
                    if (lang === 'javascript') return `// Node "${action.oldValue}" renamed to "${action.newValue}"`;
                    if (lang === 'python')     return `    # Node "${action.oldValue}" renamed to "${action.newValue}"`;
                    if (lang === 'java')       return `        // Node "${action.oldValue}" renamed to "${action.newValue}"`;
                    if (lang === 'cpp')        return `    // Node "${action.oldValue}" renamed to "${action.newValue}"`;
                }
                if (action.type === 'clear') {
                    if (lang === 'javascript') return `// List was cleared at this point`;
                    if (lang === 'python')     return `    # List was cleared at this point`;
                    if (lang === 'java')       return `        // List was cleared at this point`;
                    if (lang === 'cpp')        return `    // List was cleared at this point`;
                }
                return '';
            }).filter(Boolean).join('\n');
        };

        const codes = {
            javascript: `// ${listType === 'singly' ? 'Singly' : 'Doubly'} Linked List in JavaScript
class ListNode {
    constructor(value) {
        this.value = value;
        this.next = null;${listType === 'doubly' ? '\n        this.prev = null;' : ''}
    }
}

class LinkedList {
    constructor() {
        this.head = null;
        this.size = 0;
    }

    append(value) {
        const newNode = new ListNode(value);
        if (!this.head) {
            this.head = newNode;
        } else {
            let current = this.head;
            while (current.next) current = current.next;
            current.next = newNode;${listType === 'doubly' ? '\n            newNode.prev = current;' : ''}
        }
        this.size++;
    }

    insertAfter(targetValue, value) {
        let current = this.head;
        while (current && current.value !== targetValue) current = current.next;
        if (!current) return;
        const newNode = new ListNode(value);
        newNode.next = current.next;${listType === 'doubly' ? '\n        if (current.next) current.next.prev = newNode;\n        newNode.prev = current;' : ''}
        current.next = newNode;
        this.size++;
    }

    prepend(value) {
        const newNode = new ListNode(value);
        if (this.head) {
            newNode.next = this.head;${listType === 'doubly' ? '\n            this.head.prev = newNode;' : ''}
        }
        this.head = newNode;
        this.size++;
    }

    delete(value) {
        if (!this.head) return false;
        if (this.head.value === value) {
            this.head = this.head.next;${listType === 'doubly' ? '\n            if (this.head) this.head.prev = null;' : ''}
            this.size--;
            return true;
        }
        let current = this.head;
        while (current.next && current.next.value !== value) current = current.next;
        if (current.next) {
            current.next = current.next.next;${listType === 'doubly' ? '\n            if (current.next) current.next.prev = current;' : ''}
            this.size--;
            return true;
        }
        return false;
    }

    find(value) {
        let current = this.head;
        while (current) {
            if (current.value === value) return current;
            current = current.next;
        }
        return null;
    }

    display() {
        const values = [];
        let current = this.head;
        while (current) { values.push(current.value); current = current.next; }
        return values;
    }
}

// Main
const myList = new LinkedList();
${renderActions('javascript')}`,

            python: `# ${listType === 'singly' ? 'Singly' : 'Doubly'} Linked List in Python
class ListNode:
    def __init__(self, value):
        self.value = value
        self.next = None${listType === 'doubly' ? '\n        self.prev = None' : ''}

class LinkedList:
    def __init__(self):
        self.head = None
        self.size = 0

    def append(self, value):
        new_node = ListNode(value)
        if not self.head:
            self.head = new_node
        else:
            current = self.head
            while current.next:
                current = current.next
            current.next = new_node${listType === 'doubly' ? '\n            new_node.prev = current' : ''}
        self.size += 1

    def insert_after(self, target_value, value):
        current = self.head
        while current and current.value != target_value:
            current = current.next
        if not current:
            return
        new_node = ListNode(value)
        new_node.next = current.next${listType === 'doubly' ? '\n        if current.next:\n            current.next.prev = new_node\n        new_node.prev = current' : ''}
        current.next = new_node
        self.size += 1

    def prepend(self, value):
        new_node = ListNode(value)
        if self.head:
            new_node.next = self.head${listType === 'doubly' ? '\n            self.head.prev = new_node' : ''}
        self.head = new_node
        self.size += 1

    def delete(self, value):
        if not self.head:
            return False
        if self.head.value == value:
            self.head = self.head.next${listType === 'doubly' ? '\n            if self.head:\n                self.head.prev = None' : ''}
            self.size -= 1
            return True
        current = self.head
        while current.next and current.next.value != value:
            current = current.next
        if current.next:
            current.next = current.next.next${listType === 'doubly' ? '\n            if current.next:\n                current.next.prev = current' : ''}
            self.size -= 1
            return True
        return False

    def find(self, value):
        current = self.head
        while current:
            if current.value == value:
                return current
            current = current.next
        return None

    def display(self):
        values = []
        current = self.head
        while current:
            values.append(current.value)
            current = current.next
        return values

# Main
if __name__ == "__main__":
    my_list = LinkedList()
${renderActions('python')}`,

            java: `// ${listType === 'singly' ? 'Singly' : 'Doubly'} Linked List in Java
import java.util.*;

class ListNode<T> {
    T value;
    ListNode<T> next;${listType === 'doubly' ? '\n    ListNode<T> prev;' : ''}

    public ListNode(T value) {
        this.value = value;
        this.next = null;${listType === 'doubly' ? '\n        this.prev = null;' : ''}
    }
}

class LinkedList<T> {
    private ListNode<T> head;
    private int size;

    public LinkedList() { this.head = null; this.size = 0; }

    public void append(T value) {
        ListNode<T> newNode = new ListNode<>(value);
        if (head == null) { head = newNode; }
        else {
            ListNode<T> current = head;
            while (current.next != null) current = current.next;
            current.next = newNode;${listType === 'doubly' ? '\n            newNode.prev = current;' : ''}
        }
        size++;
    }

    public void insertAfter(T targetValue, T value) {
        ListNode<T> current = head;
        while (current != null && !current.value.equals(targetValue)) current = current.next;
        if (current == null) return;
        ListNode<T> newNode = new ListNode<>(value);
        newNode.next = current.next;${listType === 'doubly' ? '\n        if (current.next != null) current.next.prev = newNode;\n        newNode.prev = current;' : ''}
        current.next = newNode;
        size++;
    }

    public void prepend(T value) {
        ListNode<T> newNode = new ListNode<>(value);
        if (head != null) { newNode.next = head;${listType === 'doubly' ? ' head.prev = newNode;' : ''} }
        head = newNode;
        size++;
    }

    public boolean delete(T value) {
        if (head == null) return false;
        if (head.value.equals(value)) {
            head = head.next;${listType === 'doubly' ? '\n            if (head != null) head.prev = null;' : ''}
            size--; return true;
        }
        ListNode<T> current = head;
        while (current.next != null && !current.next.value.equals(value)) current = current.next;
        if (current.next != null) {
            current.next = current.next.next;${listType === 'doubly' ? '\n            if (current.next != null) current.next.prev = current;' : ''}
            size--; return true;
        }
        return false;
    }

    public ListNode<T> find(T value) {
        ListNode<T> current = head;
        while (current != null) {
            if (current.value.equals(value)) return current;
            current = current.next;
        }
        return null;
    }

    public int size() { return size; }
}

public class Main {
    public static void main(String[] args) {
        LinkedList<String> myList = new LinkedList<>();
${renderActions('java')}
    }
}`,

            cpp: `// ${listType === 'singly' ? 'Singly' : 'Doubly'} Linked List in C++
#include <iostream>
#include <string>
using namespace std;

template<typename T>
struct ListNode {
    T value;
    ListNode<T>* next;${listType === 'doubly' ? '\n    ListNode<T>* prev;' : ''}
    ListNode(const T& val) : value(val), next(nullptr)${listType === 'doubly' ? ', prev(nullptr)' : ''} {}
};

template<typename T>
class LinkedList {
private:
    ListNode<T>* head;
    int size;
public:
    LinkedList() : head(nullptr), size(0) {}

    ~LinkedList() {
        while (head) { ListNode<T>* tmp = head; head = head->next; delete tmp; }
    }

    void append(const T& value) {
        ListNode<T>* newNode = new ListNode<T>(value);
        if (!head) { head = newNode; }
        else {
            ListNode<T>* current = head;
            while (current->next) current = current->next;
            current->next = newNode;${listType === 'doubly' ? '\n            newNode->prev = current;' : ''}
        }
        size++;
    }

    void insertAfter(const T& targetValue, const T& value) {
        ListNode<T>* current = head;
        while (current && current->value != targetValue) current = current->next;
        if (!current) return;
        ListNode<T>* newNode = new ListNode<T>(value);
        newNode->next = current->next;${listType === 'doubly' ? '\n        if (current->next) current->next->prev = newNode;\n        newNode->prev = current;' : ''}
        current->next = newNode;
        size++;
    }

    void prepend(const T& value) {
        ListNode<T>* newNode = new ListNode<T>(value);
        if (head) { newNode->next = head;${listType === 'doubly' ? ' head->prev = newNode;' : ''} }
        head = newNode;
        size++;
    }

    bool deleteValue(const T& value) {
        if (!head) return false;
        if (head->value == value) {
            ListNode<T>* tmp = head; head = head->next;${listType === 'doubly' ? '\n            if (head) head->prev = nullptr;' : ''}
            delete tmp; size--; return true;
        }
        ListNode<T>* current = head;
        while (current->next && current->next->value != value) current = current->next;
        if (current->next) {
            ListNode<T>* tmp = current->next;
            current->next = current->next->next;${listType === 'doubly' ? '\n            if (current->next) current->next->prev = current;' : ''}
            delete tmp; size--; return true;
        }
        return false;
    }

    ListNode<T>* find(const T& value) {
        ListNode<T>* current = head;
        while (current) { if (current->value == value) return current; current = current->next; }
        return nullptr;
    }

    int getSize() const { return size; }
};

int main() {
    LinkedList<string> myList;
${renderActions('cpp')}
    return 0;
}`
        };
        return codes[codeLanguage];
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div className={`linked-list-container ${darkMode ? 'dark' : 'light'}`}>
            <Col className="linked-list-main">
                <h1 className="linked-list-title">
                    {selLanguage ? "Δημιουργία Λίστας" : "List Builder"}
                </h1>

                {/* List Type Selector */}
                <div className="linked-list-type-selector">
                    <button className="linked-list-button hint-button" onMouseOver={() => setShowInfo(true)} onMouseOut={() => setShowInfo(false)}>💡</button>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        {showInfo && (
                            <div className="linked-list-tooltip" onMouseEnter={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)}>
                                <div className="linked-list-tooltip-arrow" />
                                <h3 className="linked-list-tooltip-title">{selLanguage ? "Οδηγίες Χρήσης" : "Instructions"}</h3>
                                <div className="linked-list-tooltip-content">
                                    <div className="linked-list-tooltip-item"><strong>Click:</strong> {selLanguage ? "Επιλέξτε έναν κόμβο" : "Select a node"}</div>
                                    <div className="linked-list-tooltip-item"><strong>Double-click:</strong> {selLanguage ? "Επεξεργασία τιμής κόμβου" : "Edit node value"}</div>
                                    <div className="linked-list-tooltip-item"><strong>Right-click:</strong> {selLanguage ? "Εισαγωγή μετά τον κόμβο" : "Insert after node"}</div>
                                    <div className="linked-list-tooltip-item"><strong>Drag node:</strong> {selLanguage ? "Μετακίνηση κόμβου" : "Move node"}</div>
                                    <div className="linked-list-tooltip-item"><strong>Drag canvas:</strong> {selLanguage ? "Μετακίνηση λίστας" : "Pan the list"}</div>
                                    <div className="linked-list-tooltip-item"><strong>Scroll:</strong> {selLanguage ? "Ζουμ" : "Zoom"}</div>
                                    <div className="linked-list-tooltip-item"><strong>Ctrl+Enter:</strong> {selLanguage ? "Προσθήκη νέου κόμβου" : "Add new node"}</div>
                                    <div className="linked-list-tooltip-item"><strong>Ctrl+R:</strong> {selLanguage ? "Τακτοποίηση κόμβων" : "Arrange nodes"}</div>
                                </div>
                            </div>
                        )}
                    </div>
                    <button className={`linked-list-button ${listType === 'singly' ? 'active' : ''}`} onClick={() => setListType('singly')}>
                        <ArrowRight size={16} /><span>{selLanguage ? "Απλά συνδεδεμένη" : "Singly Linked List"}</span>
                    </button>
                    <button className={`linked-list-button ${listType === 'doubly' ? 'active' : ''}`} onClick={() => setListType('doubly')}>
                        <ArrowLeftRight size={16} /><span>{selLanguage ? "Διπλά συνδεδεμένη" : "Doubly Linked List"}</span>
                    </button>
                </div>

                {/* Input */}
                <div className="linked-list-input-container">
                    <input
                        type="text"
                        value={newNodeValue}
                        onChange={(e) => setNewNodeValue(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addNode(newNodeValue)}
                        placeholder={selLanguage ? "Δώσε την τιμή του κόμβου..." : "Enter node value..."}
                        className="linked-list-input"
                    />
                </div>

                {/* Operation Buttons */}
                <div className="linked-list-operations-grid">
                    <button onClick={() => addNode(newNodeValue)} className="linked-list-button">
                        <Plus size={16} /><span>{selLanguage ? "Προσθήκη Κόμβου" : "Add Node"}</span>
                    </button>

                    <button
                        onClick={() => setDeleteMode(prev => !prev)}
                        disabled={nodes.length <= 1}
                        className="linked-list-button"
                    >
                        <Trash2 size={16} />
                        <span>{selLanguage ? 'Διαγραφή Κόμβου' : 'Delete Node'}</span>
                    </button>

                    <button onClick={arrangeNodes} className="linked-list-button">
                        <RotateCcw size={16} /><span>{selLanguage ? "Οργάνωση" : "Arrange"}</span>
                    </button>

                    <button onClick={clearAll} disabled={nodes.length <= 1} className="linked-list-button">
                        <Trash2 size={16} /><span>{selLanguage ? "Καθαρισμός" : "Restart"}</span>
                    </button>
                </div>

                {/* Canvas wrapper */}
                <div style={{ position: 'relative' }}>

                    {/* Zoom Controls — white card, black symbols */}
                    <div style={{
                        position: 'absolute', bottom: '14px', left: '14px', zIndex: 20,
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        background: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden',
                        width: '32px',
                    }}>
                        <button
                            onClick={handleZoomIn}
                            title="Zoom In"
                            style={{
                                width: '32px', height: '32px',
                                border: 'none', background: 'white',
                                color: '#111827',
                                cursor: 'pointer', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                padding: 0, fontSize: '20px', fontWeight: '400',
                                lineHeight: 1,
                            }}
                        >+</button>
                        <div style={{ width: '100%', height: '1px', background: '#e2e8f0' }} />
                        <button
                            onClick={handleZoomOut}
                            title="Zoom Out"
                            style={{
                                width: '32px', height: '32px',
                                border: 'none', background: 'white',
                                color: '#111827',
                                cursor: 'pointer', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                padding: 0, fontSize: '22px', fontWeight: '400',
                                lineHeight: 1,
                            }}
                        >−</button>
                    </div>

                    {/* Canvas */}
                    <div
                        ref={containerRef}
                        className={`linked-list-canvas ${!darkMode ? 'light-border' : ''}`}
                        style={{
                            overflow: 'hidden',
                            cursor: deleteMode ? 'crosshair' : isPanning ? 'grabbing' : 'grab',
                        }}
                        onMouseDown={handleCanvasMouseDown}
                    >
                        {/* Pan + Zoom layer */}
                        <div style={{
                            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                            transformOrigin: 'top left',
                            width:  `${100 / zoom}%`,
                            height: `${100 / zoom}%`,
                            position: 'relative',
                        }}>
                            {renderConnections()}

                            {nodes.map((node, index) => (
                                <div
                                    key={node.id}
                                    onMouseDown={(e) => handleMouseDown(e, node)}
                                    onClick={(e) => handleNodeClick(e, node)}
                                    onDoubleClick={() => !deleteMode && setEditingId(node.id)}
                                    onContextMenu={(e) => {
                                        e.preventDefault();
                                        if (!deleteMode) {
                                            setSelectedNode(node.id);
                                            if (selectedNode === node.id && nodes.length > 1) insertAfter(node.id);
                                        }
                                    }}
                                    style={{
                                        position: 'absolute',
                                        left: node.x,
                                        top: node.y,
                                        width: nodeWidth,
                                        height: nodeHeight,
                                        border: `3px solid ${selectedNode === node.id ? '#5a6fd8' : '#cbd5e0'}`,
                                        borderRadius: '12px',
                                        cursor: deleteMode ? 'pointer' : 'grab',
                                        zIndex: 10,
                                        transition: 'box-shadow 0.2s, border 0.2s',
                                        transform: animatingNode?.nodeId === node.id
                                            ? 'scale(1.05) translateY(-8px)'
                                            : hoverNode === node.id
                                                ? 'scale(1.02) translateY(-3px)'
                                                : 'scale(1)',
                                        boxShadow: selectedNode === node.id
                                            ? '0 10px 25px rgba(102, 126, 234, 0.4)'
                                            : '0 5px 15px rgba(0,0,0,0.1)',
                                        background: selectedNode === node.id
                                            ? 'linear-gradient(135deg, #667eea, #764ba2)'
                                            : node.color
                                                ? `linear-gradient(135deg, ${node.color}, ${node.color}dd)`
                                                : 'linear-gradient(135deg, #ffffff, #f8f9fa)'
                                    }}
                                    onMouseEnter={() => setHoverNode(node.id)}
                                    onMouseLeave={() => setHoverNode(null)}
                                >
                                    {index === 0 && (
                                        <div style={{
                                            position: 'absolute', top: '-10px', left: '-10px',
                                            background: 'linear-gradient(135deg, #10b981, #059669)',
                                            color: 'white', borderRadius: '50%', width: '24px', height: '24px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '8px', fontWeight: '700',
                                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)', border: '2px solid white'
                                        }}>HEAD</div>
                                    )}

                                    <div style={{
                                        margin: '8px 8px 20px 8px',
                                        height: nodeHeight - 35,
                                        backgroundColor: selectedNode === node.id ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.9)',
                                        border: `1px solid ${selectedNode === node.id ? 'rgba(255,255,255,0.3)' : '#e2e8f0'}`,
                                        borderRadius: '8px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        fontSize: '12px', fontWeight: '600',
                                        color: selectedNode === node.id ? '#ffffff' : '#2d3748',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        padding: '0 6px'
                                    }}>
                                        {editingId === node.id ? (
                                            <input
                                                type="text"
                                                defaultValue={node.value}
                                                style={{ width: '100%', border: 'none', background: 'transparent', color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', textAlign: 'center', outline: 'none' }}
                                                autoFocus
                                                onBlur={(e) => editNode(node.id, e.target.value)}
                                                onKeyPress={(e) => { if (e.key === 'Enter') editNode(node.id, e.target.value); }}
                                            />
                                        ) : (
                                            <>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{node.value}</span>
                                                <div style={{ width: '1px', height: '20px', backgroundColor: selectedNode === node.id ? 'rgba(255,255,255,0.5)' : '#cbd5e0', margin: '0 6px' }} />
                                                <span style={{ fontSize: '10px', opacity: 0.9 }}>{index < nodes.length - 1 ? 'next' : 'null'}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {animatingNode && (
                                <div className="linked-list-animation-indicator">
                                    {animatingNode.operation === 'add'     && '➕ ADDING'}
                                    {animatingNode.operation === 'insert'  && '🔗 INSERTING'}
                                    {animatingNode.operation === 'delete'  && '❌ DELETING'}
                                    {animatingNode.operation === 'arrange' && '📐 ARRANGING'}
                                    {animatingNode.operation === 'clear'   && '🗑️ CLEARING'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Language Selector */}
                <div className="linked-list-language-section">
                    <label className="linked-list-label">{selLanguage ? "Γλώσσα Προγραμματισμού" : "Programming Language"}</label>
                    <select value={codeLanguage} onChange={(e) => setCodeLanguage(e.target.value)} className="linked-list-select">
                        <option value="javascript">JavaScript</option>
                        <option value="python">Python</option>
                        <option value="java">Java</option>
                        <option value="cpp">C++</option>
                    </select>
                </div>

                {/* Code Display */}
                <div className="linked-list-code-display">
                    <CodeHighlighter code={generateCode()} language={codeLanguage} />
                </div>

                {/* Copy Button */}
                <button
                    onClick={() => { navigator.clipboard.writeText(generateCode()); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className={`linked-list-copy-button ${copied ? 'copied' : 'not-copied'}`}
                >
                    <Copy size={16} />
                    <span>{copied ? (selLanguage ? 'Αντιγράφηκε!' : 'Copied') : (selLanguage ? 'Αντιγραφή' : 'Copy')}</span>
                </button>
            </Col>
        </div>
    );
};

export default LinkedListBuilder;